import { Button, Divider, Typography, message, Space } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import React, { FC, useCallback, useRef, useState } from 'react';
import { Form, FormSchema, SchemaFormItems } from '../../components/form/Form';
import { useConnection } from '../../connection/Application';
import styles from './signup.module.less';
import AuthLayout from './AuthLayout';
import Router, { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';
import { DEFAULT_OG_IMAGES, SEO_COMMON } from '../../layout/candidate/CandidateLayout';
import Utils from '../../utils/Utils';
import config from '../../config';
import ReCAPTCHA from 'react-google-recaptcha';
import { FlexBox } from '../../components/FlexBox';

const { Title } = Typography;
const ReCapt: any = ReCAPTCHA;

const emailSchema: FormSchema = [
  {
    id: 'email',
    type: 'string',
    name: 'Email Address',
    props: { placeholder: 'example@email.com' },
    options: {
      rules: [
        { required: true, message: 'Email is required' },
        { type: 'email', message: 'Invalid Email' },
      ],
    },
  },
];

const otpSchema: FormSchema = [
  {
    id: 'otp',
    type: 'string',
    name: 'OTP Code',
    props: { placeholder: 'Enter 6-digit OTP code', maxLength: 6 },
    options: {
      rules: [
        { required: true, message: 'OTP is required' },
        { len: 6, message: 'OTP must be 6 digits' },
        { pattern: /^\d{6}$/, message: 'OTP must contain only numbers' },
      ],
    },
  },
];

const OtpLoginPage: FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [emailForm] = useForm();
  const [otpForm] = useForm();
  const [step, setStep] = useState<'email' | 'otp'>('email');
  const [otpId, setOtpId] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [captchaKey, setCaptchaKey] = useState<number>(0);

  const history = useRouter();
  const connection = useConnection();
  const recaptchaRef = useRef<any>(null);

  const resetCaptcha = useCallback(() => {
    try {
      recaptchaRef.current?.reset();
    } catch (e) {
      console.log(e);
    }
    recaptchaRef.current = null;
    setCaptchaKey((prev) => prev + 1);
  }, []);

  const getCaptchaToken = useCallback(async () => {
    const ref = recaptchaRef.current;

    if (!ref) {
      throw new Error('reCAPTCHA is not ready yet. Please try again.');
    }

    const token = await ref.executeAsync();

    if (!token || !token.trim()) {
      throw new Error('reCAPTCHA verification failed. Please try again.');
    }

    return token;
  }, []);

  const onEmailSubmit = useCallback(
    async (formData) => {
      try {
        setLoading(true);

        const reCaptchaToken = await getCaptchaToken();
        setEmail(formData.email);

        const response = await connection.post('admin/check-login-email', {
          email: formData.email,
          reCaptchaToken,
        });

        if (!response) {
          throw new Error('Email not found. Please check your email address.');
        }

        setOtpId(response.otpId);
        setStep('otp');
        message.success('OTP sent to your email address');
      } catch (error: any) {
        const errorMessage =
          error?.body?.message || error?.message || 'Failed to send OTP. Please try again.';
        emailForm.setFields([{ name: 'email', errors: [errorMessage] }]);
      } finally {
        resetCaptcha();
        setLoading(false);
      }
    },
    [connection, emailForm, getCaptchaToken, resetCaptcha],
  );

  const onOtpSubmit = useCallback(
    async (formData) => {
      try {
        setLoading(true);

        const response = await connection.post('admin/verify-login-otp', {
          otpId,
          otp: formData.otp,
        });

        const user = connection.setToken(
          response.token.accessToken,
          response.token.refreshToken,
          false,
        );

        Utils.sendGtmCustomEvent(
          'login',
          { loginMethod: 'otp' },
          {
            userId: user?.userId ?? 'guest',
            envId: config.envId,
          },
        );

        if (localStorage.getItem('BackUrl')) {
          Router.push({
            pathname: localStorage.getItem('BackUrl') ?? '',
            query: {
              ...JSON.parse(localStorage.getItem('BackQuery') ?? '{}'),
            },
          });
          localStorage.removeItem('BackUrl');
          localStorage.removeItem('BackQuery');
        } else {
          history.push('/azend');
        }
      } catch (error: any) {
        const errorMessage =
          error?.body?.message || error?.message || 'Invalid OTP. Please try again.';
        otpForm.setFields([{ name: 'otp', errors: [errorMessage] }]);
      } finally {
        setLoading(false);
      }
    },
    [connection, otpId, history, otpForm],
  );

  const handleBackToEmail = useCallback(() => {
    setStep('email');
    setEmail('');
    otpForm.resetFields();
    resetCaptcha();
  }, [otpForm, resetCaptcha]);

  const handleResendOtp = useCallback(async () => {
    try {
      setLoading(true);

      const reCaptchaToken = await getCaptchaToken();

      const response = await connection.post('admin/login/request-otp', {
        email,
        reCaptchaToken,
      });

      if (response?.otpId) {
        setOtpId(response.otpId);
      }

      message.success('OTP resent to your email address');
    } catch (error: any) {
      const errorMessage =
        error?.body?.message || error?.message || 'Failed to resend OTP. Please try again.';
      message.error(errorMessage);
    } finally {
      resetCaptcha();
      setLoading(false);
    }
  }, [connection, email, getCaptchaToken, resetCaptcha]);

  return (
    <>
      <NextSeo
        canonical="https://www.careers360.lk/otp-login"
        title="OTP Login | Careers360"
        description="Sign in to Careers360 using OTP verification for secure access."
        additionalMetaTags={[
          { name: 'keywords', content: 'OTP Login | Careers360 | secure login' },
        ]}
        openGraph={{
          title: `OTP Login | Careers360`,
          description: 'Sign in to Careers360 using OTP verification for secure access.',
          images: DEFAULT_OG_IMAGES,
        }}
        {...SEO_COMMON}
      />

      <AuthLayout>
        <Title level={3} className={styles.title}>
          {step === 'email' ? 'Sign In with OTP' : 'Enter OTP'}
        </Title>

        <Typography className={styles.subTitle} style={{ marginBottom: 20 }}>
          {step === 'email'
            ? 'Enter your email address to receive an OTP code'
            : `We've sent a 6-digit code to ${email}`}
        </Typography>

        {step === 'email' ? (
          <Form
            form={emailForm}
            onFinish={onEmailSubmit}
            layout="vertical"
            style={{ marginTop: 34 }}>
            <SchemaFormItems schema={emailSchema} />
            <Button
              style={{ marginTop: 16, fontSize: 20 }}
              htmlType="submit"
              type="primary"
              block
              loading={loading}>
              Send OTP
            </Button>
          </Form>
        ) : (
          <Form form={otpForm} onFinish={onOtpSubmit} layout="vertical" style={{ marginTop: 10 }}>
            <Button
              type="link"
              onClick={handleBackToEmail}
              style={{ padding: 0, marginBottom: 10 }}>
              Back to Email
            </Button>

            <SchemaFormItems schema={otpSchema} />

            <Button
              style={{ marginTop: 16, fontSize: 20 }}
              htmlType="submit"
              type="primary"
              block
              loading={loading}>
              Verify OTP
            </Button>

            <Space direction="vertical" style={{ width: '100%', marginTop: 10 }}>
              <Button
                type="link"
                onClick={handleResendOtp}
                loading={loading}
                style={{ padding: 0 }}>
                Resend OTP
              </Button>
            </Space>
          </Form>
        )}

        <div style={{ position: 'absolute', left: '-9999px', bottom: 0 }}>
          <ReCapt
            key={captchaKey}
            ref={recaptchaRef}
            sitekey={config.recaptchaSiteKey}
            size="invisible"
          />
        </div>

        <Divider plain style={{ marginTop: 32, marginBottom: 5 }}>
          <Typography style={{ color: '#6C6C6C', fontSize: 14 }}>or</Typography>
        </Divider>

        <div className={styles.signupcontainer}>
          <Space style={{ marginTop: 4 }}>
            <Typography style={{ color: '#5B6272', fontSize: 14 }}>Use regular login?</Typography>
            <Button
              type="link"
              onClick={() => history.push('/login')}
              style={{ padding: 0, fontWeight: 'bold' }}>
              Sign In with Password
            </Button>
          </Space>
        </div>
      </AuthLayout>
    </>
  );
};

export default OtpLoginPage;
